import { useQuery, useApolloClient } from "@apollo/client";
import { LIST_STUDIOS, BOOKINGS_BY_STUDIO_ID } from "../../graphql/queries/calendarQueries";
import { useUser } from "../useUser";
import { useState, useEffect } from "react";
import { getCloudFrontImageUrl } from "../../util/cloudFrontUtils";
import { formatBerlinDate, formatBerlinTime } from "../../util/bookingDateTime";
import { filterHostVisibleStudios } from "../../util/studioStatus";
import { buildCoarseLocationText } from "../../util/locationPrivacy";

export const useFetchStudioData = (userID) => {
  const { user } = useUser(userID);
  const client = useApolloClient();
  const [formattedStudioData, setFormattedStudioData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Query to fetch studios owned by the current user
  const { data: studiosData, loading: studiosLoading, error: studiosError } = useQuery(LIST_STUDIOS, {
    variables: {
      filter: {
        userID: { eq: userID }
      }
    },
    skip: !userID
  });

  // Function to fetch bookings for a specific studio
  const fetchBookingsForStudio = async (studioId) => {
    try {
      
      const { data } = await client.query({
        query: BOOKINGS_BY_STUDIO_ID,
        variables: {
          studioID: studioId
        }
      });
            
      if (!data || !data.bookingsByStudioID || !data.bookingsByStudioID.items) {
        return [];
      }
      
      return data.bookingsByStudioID.items || [];
    } catch (err) {
      return [];
    }
  };

  // Process and format the data when studios data is loaded
  useEffect(() => {
    const processStudioData = async () => {
      if (studiosLoading) return;
      
      if (studiosError) {
        setError(studiosError);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        const visibleStudios = filterHostVisibleStudios(studiosData?.listStudios?.items);
        if (!visibleStudios.length) {
          setFormattedStudioData([]);
          setLoading(false);
          return;
        }
        
        // Process each studio and its bookings
        const processedStudios = await Promise.all(
          visibleStudios.map(async (studio) => {
            
            // Fetch bookings for this studio
            const bookings = await fetchBookingsForStudio(studio.id);
            
            // Format bookings to match the expected structure
            const formattedBookings = bookings.map(booking => {
              const startDate = new Date(booking.startDateTime);
              const endDate = new Date(booking.endDateTime);
              
              // Process user profile picture if available
              const processedProfilePicture = booking.User?.profilePicture ? 
                getCloudFrontImageUrl(booking.User.profilePicture, { width: 100, height: 100, fit: 'cover' }) : 
                null;
              
              return {
                date: formatBerlinDate(startDate, { day: '2-digit', month: '2-digit', year: 'numeric' }),
                projectName: booking.projectName,
                type: booking?.projectName ? "Buchung" : "Anfrage",
                status: booking?.status,
                id: booking?.id,
                chatRoomID: booking?.chatRoomID,
                user: booking?.User ? `${booking?.User?.firstName} ${booking?.User?.lastName?.charAt(0)}.` : "Unbekannt",
                startDateTime: booking?.startDateTime,
                endDateTime: booking?.endDateTime,
                startTime: formatBerlinTime(startDate, { hour: '2-digit', minute: '2-digit' }),
                endTime: formatBerlinTime(endDate, { hour: '2-digit', minute: '2-digit' }),
                price: `${booking.totalPrice} Euro`,
                studioLocation: buildCoarseLocationText(studio) || "Unbekannt",
                // Add full user data
                userData: booking.User ? {
                  id: booking.User.id,
                  firstName: booking.User.firstName,
                  lastName: booking.User.lastName,
                  email: booking.User.email,
                  phoneNumber: booking.User.phoneNumber,
                  profilePicture: booking.User.profilePicture,
                  processedProfilePicture: processedProfilePicture
                } : null,
                // Add studio data
                studioData: {
                  minNumberOfHours: studio.minNumberOfHours,
                  id: studio.id,
                  name: studio.StudioName,
                  studioImage: studio.images && studio.images.length > 0 
                    ? getCloudFrontImageUrl(studio.images[0], { width: 400, height: 300, fit: 'cover' }) 
                    : null
                }
              };
            });

            const studioImage = studio.images && studio.images.length > 0 
              ? getCloudFrontImageUrl(studio.images[0], { width: 400, height: 300, fit: 'cover' }) 
              : null;
            
            return {
              minNumberOfHours: studio.minNumberOfHours,
              studioId: studio.id,
              image: studioImage,
              name: studio.StudioName,
              location: buildCoarseLocationText(studio) || "Unbekannt",
              bookings: formattedBookings
            };
          })
        );

        setFormattedStudioData(processedStudios);
      } catch (err) {
        console.error("Error processing studio data:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    processStudioData();
  }, [studiosData, studiosLoading, studiosError, user, client]);

  return {
    studioData: formattedStudioData,
    loading,
    error
  };
}; 
